import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDecisionDialog } from '@frontend/elliemae-helpers';
import { useAdjustments } from '@frontend/pricing-search';
import { useNavigationBlocker } from '@frontend/utils';
import { RequestActionType } from '../../common/constants/enumTypes';
import Session, {
  ALL_SUMMARY_PARAMS,
  IS_BUYSIDE,
  IS_SELLSIDE,
  IS_VALIDATE,
} from '../../common/services/Session';
import { buysideRequested } from '../../common/utils/constants/ErrorCodes';
import { useIsZeroBasedPricing } from '../../common/utils/customHooks/useIsZeroBasedPricing';
import { stopLog } from '../../common/utils/logger';
import { isEmpty } from '../../common/utils/shared';
import {
  getBuysideDataAction,
  getRateSelectorAction,
  getSettingAction,
  getValidateDataAction,
} from '../../data/lockSummary/actionCreators';
import { closeLoader, openLoader } from '../../data/screenLoader/actions';
import { AppDispatch } from '../../store';
import { setLoanDataValue } from '../components/Form/utils/common';
import {
  StyledEppsContainerBody,
  StyledEppsParentView,
  StyledEppsWrapper,
} from '../global/styles/styled';
import { CompareRates } from './components/CompareRates';
import { FooterActionButtons } from './components/FooterActionButtons';
import { LockConfirm } from './components/LockConfirm';
import { loanBuySideAdjustmentsSelector } from '../components/PricingGrid/selectors';
import { PendingAdjustmentsDialog } from './pendingAdjustmentsDialog';
import { setBuySideAdjustmentsAction } from '../../data/origin/actionCreators';

export const LockConfirmWrapper = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isCompareRatesView, setIsCompareRatesView] = useState(false);
  const [initialLoanVariations, setInitialLoanVariations] = useState(null);
  const isValidate = Session.get(IS_VALIDATE);
  const isBuySide = Session.get(IS_BUYSIDE);
  const isSellSide = Session.get(IS_SELLSIDE);
  const userWentToSearchResult = !isEmpty(Session.get(ALL_SUMMARY_PARAMS));
  const navigate = useNavigate();
  const isZeroBasedPricing = useIsZeroBasedPricing();
  const { hasPendingAdjustments, setHasPendingAdjustments } = useAdjustments();
  const loanVariations = useSelector(loanBuySideAdjustmentsSelector());
  const { isOpen, toggleDialog } = useDecisionDialog();
  const { confirmNavigation, cancelNavigation } = useNavigationBlocker({
    shouldBlock: hasPendingAdjustments,
    onNavigationBlocked: toggleDialog,
    blockBrowserNavigation: true,
  });

  const initData = async () => {
    if (!userWentToSearchResult) {
      await dispatch(getSettingAction());
      if (isValidate) {
        await dispatch(getValidateDataAction(navigate, isZeroBasedPricing));
      }
      if (!isValidate) {
        await dispatch(getBuysideDataAction(navigate, isZeroBasedPricing));
      }
    }
    if (userWentToSearchResult) {
      dispatch(openLoader('Loading'));
      await dispatch(getRateSelectorAction());
      dispatch(closeLoader());
    }
  };

  const handleConfirmNavigation = () => {
    setHasPendingAdjustments(false);
    dispatch(setBuySideAdjustmentsAction(initialLoanVariations));
    toggleDialog();
    confirmNavigation();
  };

  const handleCancelNavigation = () => {
    toggleDialog();
    cancelNavigation();
  };

  useEffect(() => {
    if (loanVariations && !initialLoanVariations) {
      setInitialLoanVariations(loanVariations);
    }
  }, [loanVariations, initialLoanVariations]);

  useEffect(() => {
    if (isBuySide || isSellSide || isValidate)
      dispatch(
        setLoanDataValue(
          isValidate
            ? RequestActionType.ValidateLock
            : isBuySide
            ? RequestActionType.BuyRequest
            : RequestActionType.SellRequest,
          'requestAction',
        ) as any,
      );
    initData();
    stopLog(buysideRequested);
    window.scrollTo(0, 0);
  }, [isSellSide]);

  return (
    <>
      {isCompareRatesView && (
        <CompareRates handleBack={() => setIsCompareRatesView(false)} />
      )}
      {!isCompareRatesView && (
        <StyledEppsParentView>
          <StyledEppsContainerBody>
            <StyledEppsWrapper>
              <LockConfirm setIsCompareRatesView={setIsCompareRatesView} />
            </StyledEppsWrapper>
          </StyledEppsContainerBody>
          <FooterActionButtons />
        </StyledEppsParentView>
      )}
      <PendingAdjustmentsDialog
        isOpen={isOpen}
        handleClose={handleCancelNavigation}
        onAffirmation={handleConfirmNavigation}
      ></PendingAdjustmentsDialog>
    </>
  );
};
